// src/components/Ownerdashboard/ownerAuth.js
import Cookies from "js-cookie";
import apiClient from "../../common/apiClient";

const OWNER_BASE = "/owner/properties/dashboard";

export const OWNER_TOKEN_COOKIE = "ownerToken";
export const OWNER_NUMBER_COOKIE = "ownerNumber";
export const OWNER_ID_COOKIE = "ownerId";
export const OWNER_NAME_COOKIE = "ownerName";

export const getOwnerToken = () => Cookies.get(OWNER_TOKEN_COOKIE);
export const getOwnerNumber = () => Cookies.get(OWNER_NUMBER_COOKIE);
export const getOwnerName = () => Cookies.get(OWNER_NAME_COOKIE);
export const getOwnerId = () => Cookies.get(OWNER_ID_COOKIE);
export const isOwnerLoggedIn = () => Boolean(getOwnerToken());

export const saveOwnerSession = ({ token, number, owner, ownerId }) => {
  Cookies.set(OWNER_TOKEN_COOKIE, token, { expires: 365 });

  if (number) {
    Cookies.set(OWNER_NUMBER_COOKIE, number, { expires: 365 });
  }
  // verify-otp returns the id flat as `ownerId`; older shapes nest it in `owner`.
  const id = ownerId || owner?._id || owner?.id;
  if (id) {
    Cookies.set(OWNER_ID_COOKIE, id, { expires: 365 });
  }
  if (owner?.name) {
    Cookies.set(OWNER_NAME_COOKIE, owner.name, { expires: 365 });
  }
};

export const clearOwnerSession = () => {
  Cookies.remove(OWNER_TOKEN_COOKIE);
  Cookies.remove(OWNER_NUMBER_COOKIE);
  Cookies.remove(OWNER_ID_COOKIE);
  Cookies.remove(OWNER_NAME_COOKIE);
};

// Backend may wrap the payload in `data` or return it flat.
const getPayload = (response) => response?.data?.data || response?.data || {};

const pickToken = (payload) =>
  payload?.token ||
  payload?.accessToken ||
  payload?.data?.token ||
  payload?.data?.accessToken ||
  null;

const pickOwner = (payload) => payload?.owner || payload?.user || payload?.data?.owner || null;

const pickMessage = (response, fallback) => {
  if (typeof response?.error === "string") return response.error;
  return (
    response?.error?.message ||
    response?.data?.message ||
    response?.data?.error ||
    fallback
  );
};

// A request is only a success when the transport succeeded *and* the body
// did not explicitly flag a failure.
const isOk = (response) =>
  response?.success === true &&
  response?.data?.success !== false &&
  response?.data?.status !== "error";

/**
 * POST /owner/properties/dashboard/send-otp
 */
export const sendOwnerOtp = async (number) => {
  const response = await apiClient("post", `${OWNER_BASE}/send-otp`, { number });

  if (!isOk(response)) {
    return {
      success: false,
      message: pickMessage(response, "Failed to send OTP. Please try again."),
    };
  }

  return {
    success: true,
    message: response?.data?.message || "OTP sent successfully.",
    data: getPayload(response),
  };
};

/**
 * POST /owner/properties/dashboard/verify-otp
 * On success the token is stored in cookies.
 */
export const verifyOwnerOtp = async (number, otp) => {
  const response = await apiClient("post", `${OWNER_BASE}/verify-otp`, { number, otp });

  if (!isOk(response)) {
    return {
      success: false,
      message: pickMessage(response, "Invalid OTP. Please try again."),
    };
  }

  const payload = getPayload(response);
  const token = pickToken(payload);

  if (!token) {
    return {
      success: false,
      message: "Verification succeeded but no token was returned. Please try again.",
    };
  }

  const ownerId = payload?.ownerId || payload?.data?.ownerId;

  saveOwnerSession({ token, number, ownerId, owner: pickOwner(payload) });

  return {
    success: true,
    message: response?.data?.message || "OTP verified successfully.",
    token,
    ownerId,
    data: payload,
  };
};

/**
 * GET /owner/properties/dashboard?page=&limit=
 * Returns { properties, pagination, number, sessionExpiresAt }.
 */
export const fetchOwnerProperties = async ({ page = 1, limit = 20 } = {}) => {
  const token = getOwnerToken();

  if (!token) {
    return { success: false, unauthorized: true, message: "Please login again." };
  }

  const response = await apiClient(
    "get",
    OWNER_BASE,
    { page, limit },
    { headers: { Authorization: `Bearer ${token}` } }
  );

  if (!isOk(response)) {
    // The session token is single-device and expires after 24h.
    const unauthorized = response?.status === 401;
    if (unauthorized) clearOwnerSession();

    return {
      success: false,
      unauthorized,
      message: pickMessage(response, "Failed to fetch properties."),
    };
  }

  const payload = getPayload(response);

  return {
    success: true,
    properties: Array.isArray(payload?.properties) ? payload.properties : [],
    pagination: payload?.pagination || {},
    // Server-computed dashboard counters: totalProperties, pendingUpdate,
    // liveUpdate, rejectedUpdate, neverUpdated, totalSubmissions...
    stats: payload?.stats || null,
    number: payload?.number,
    sessionExpiresAt: payload?.sessionExpiresAt,
  };
};

/**
 * POST /owner/properties/dashboard/update-property
 * The payload must carry PropertyObjectId (or PropertyId) plus the edited fields.
 * Returns { changedFields, property } as computed by the server.
 */
export const updateOwnerProperty = async (payload) => {
  const token = getOwnerToken();

  if (!token) {
    return { success: false, unauthorized: true, message: "Please login again." };
  }

  const response = await apiClient("post", `${OWNER_BASE}/update-property`, payload, {
    headers: { Authorization: `Bearer ${token}` },
  });

  if (!isOk(response)) {
    const unauthorized = response?.status === 401;
    if (unauthorized) clearOwnerSession();

    return {
      success: false,
      unauthorized,
      message: pickMessage(response, "Failed to save the property update."),
    };
  }

  const payloadData = getPayload(response);

  return {
    success: true,
    message: response?.data?.message || "Property update saved successfully.",
    changedFields: payloadData?.changedFields || [],
    property: payloadData?.property,
  };
};

/**
 * POST /cjidnvij/ceksfbuebijn/user/ckbwubuw/cjiwbucb/:propId/status/cajbyqwvfydgqv
 * The same status endpoint the broker property cards post to (see PropertyCard.js),
 * so an owner switching a listing to Rent out / Sell out is logged the same way.
 * The previous value travels with the new one so the server can record the change,
 * plus `Updatedby: "Owner"` so the audit trail separates owner edits from broker ones.
 */
export const updateOwnerPropertyStatus = async ({
  propId,
  userId,
  newStatus,
  previousStatus,
  statusField = "status",
}) => {
  if (!propId) {
    return { success: false, message: "This property is missing its id." };
  }

  const response = await apiClient(
    "post",
    `/cjidnvij/ceksfbuebijn/user/ckbwubuw/cjiwbucb/${propId}/status/cajbyqwvfydgqv`,
    {
      propId,
      userId,
      newStatus,
      previousStatus,
      statusField,
      // Owner-dashboard only - tells the server the owner flipped this, not a broker.
      Updatedby: "Owner",
    }
  );

  if (!isOk(response)) {
    const unauthorized = response?.status === 401;
    if (unauthorized) clearOwnerSession();

    return {
      success: false,
      unauthorized,
      message: pickMessage(response, "Failed to update the status."),
    };
  }

  return {
    success: true,
    message: response?.data?.message || "Status updated successfully.",
    data: getPayload(response),
  };
};

/**
 * POST /owner/properties/dashboard/logout — ends the server session, then the local one.
 */
export const ownerLogout = async () => {
  const token = getOwnerToken();

  if (token) {
    try {
      await apiClient(
        "post",
        `${OWNER_BASE}/logout`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch (err) {
      // The local session is cleared either way.
    }
  }

  clearOwnerSession();
};
